import React, { useState, useEffect, useRef } from 'react';

type SlideContentWrapperProps = {
  children: React.ReactNode;
  currentIndex: number;
  totalItems: number;
  onPrevious: () => void;
  onNext: () => void;
};

export const SlideContentWrapper: React.FC<SlideContentWrapperProps> = ({
  children,
  currentIndex,
  totalItems,
  onPrevious,
  onNext,
}) => {
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const prevIndexRef = useRef(currentIndex);

  // Minimum swipe distance (in pixels)
  const minSwipeDistance = 50;

  // Detect direction based on index change
  useEffect(() => {
    if (prevIndexRef.current !== currentIndex) {
      const direction = currentIndex > prevIndexRef.current ? 'left' : 'right';
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSlideDirection(direction);
      prevIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  // Reset slide direction after animation completes
  useEffect(() => {
    if (slideDirection !== null) {
      const timer = setTimeout(() => {
        setSlideDirection(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [slideDirection]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onPrevious();
    }
  };

  const handleNext = () => {
    if (currentIndex < totalItems - 1) {
      onNext();
    }
  };

  const onTouchStartHandler = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMoveHandler = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < totalItems - 1) {
      handleNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
  };

  return (
    <div
      className="relative overflow-hidden"
      onTouchStart={onTouchStartHandler}
      onTouchMove={onTouchMoveHandler}
      onTouchEnd={onTouchEndHandler}
    >
      <div
        className={`transition-transform duration-300 ease-in-out ${
          slideDirection === 'left' ? 'animate-slide-left' : slideDirection === 'right' ? 'animate-slide-right' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
};
